import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { LogOut, RefreshCw, Clock, FileText, FolderOpen, BarChart3, FileCheck } from 'lucide-react'
import { toast } from 'sonner'

export default function AdminDashboard() {
  const { admin, token, logout } = useAuth()
  const [documentsCount, setDocumentsCount] = useState<number | null>(null)
  const [reportExists, setReportExists] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [token])

  const loadData = async () => {
    if (!token) return
    setIsLoading(true)
    try {
      // Carregar documentos e relatórios em paralelo
      const [docs, reports] = await Promise.all([
        api.getAdminDocuments(token),
        api.getAdminReports(token)
      ])
      setDocumentsCount(docs.length)
      setReportExists(reports.some(r => r.isPublished))
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
      toast.error('Erro ao carregar estatísticas')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    toast.success('Logout realizado')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-serif font-bold text-gray-900">Painel Administrativo</h1>
            <p className="text-sm text-gray-500">Bem-vindo, {admin?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
      </header>

      <div className="p-6">
        {/* Cards de estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Card de Documentos */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Documentos Publicados</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {isLoading ? (
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-[#1D71C3] rounded-full animate-spin" />
                  ) : (
                    documentsCount ?? 0
                  )}
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
                <FolderOpen size={24} className="text-indigo-500" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Documentos disponíveis para download no site
            </p>
          </div>

          {/* Card de Relatórios */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Relatório Escuta Social</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {isLoading ? (
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-[#1D71C3] rounded-full animate-spin" />
                  ) : (
                    <span className={reportExists ? 'text-green-600' : 'text-gray-400'}>
                      {reportExists ? '✅' : '❌'}
                    </span>
                  )}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                reportExists ? 'bg-green-50' : 'bg-gray-100'
              }`}>
                <FileCheck size={24} className={reportExists ? 'text-green-500' : 'text-gray-400'} />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              {reportExists
                ? 'Relatório publicado e disponível para download'
                : 'Nenhum relatório publicado ainda'}
            </p>
          </div>

          {/* Card de Acessos */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Último Acesso</p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {new Date().toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Clock size={24} className="text-[#1D71C3]" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              {admin?.email}
            </p>
          </div>
        </div>

        {/* Módulos disponíveis */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <a
            href="/admin/stages"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-[#1D71C3]/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#1D71C3]/10 rounded-lg flex items-center justify-center group-hover:bg-[#1D71C3] transition-colors">
                <BarChart3 size={20} className="text-[#1D71C3] group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Gerenciar Fases</h3>
            </div>
            <p className="text-sm text-gray-500">
              Altere a fase atual do projeto e acompanhe o progresso das etapas.
            </p>
            <p className="text-xs text-[#1D71C3] mt-3 font-medium group-hover:underline">
              Gerenciar →
            </p>
          </a>

          <a
            href="/admin/documents"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-indigo-500/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center group-hover:bg-indigo-500 transition-colors">
                <FileText size={20} className="text-indigo-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Documentos</h3>
            </div>
            <p className="text-sm text-gray-500">
              Gerencie os documentos públicos do projeto. Faça upload, edite ou remova arquivos.
            </p>
            <p className="text-xs text-indigo-600 mt-3 font-medium group-hover:underline">
              Gerenciar documentos →
            </p>
          </a>

          <a
            href="/admin/reports"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-green-500/30 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center group-hover:bg-green-500 transition-colors">
                <FileCheck size={20} className="text-green-500 group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Relatório Escuta Social</h3>
            </div>
            <p className="text-sm text-gray-500">
              Gerencie o relatório da Escuta Social disponível na página inicial do site.
            </p>
            <p className="text-xs text-green-600 mt-3 font-medium group-hover:underline">
              {reportExists ? 'Atualizar relatório →' : 'Publicar relatório →'}
            </p>
          </a>

          <a
            href="/admin/logs"
            className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:border-gray-300 transition-all group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <Clock size={20} className="text-gray-500 group-hover:text-gray-700" />
              </div>
              <h3 className="font-semibold text-gray-900">Logs do Sistema</h3>
            </div>
            <p className="text-sm text-gray-500">
              Visualize o histórico de ações dos administradores no sistema.
            </p>
            <p className="text-xs text-gray-500 mt-3 font-medium group-hover:underline">
              Ver logs →
            </p>
          </a>
        </div>

        {/* Botão de atualizar */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={loadData}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-[#1D71C3] transition-colors"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Atualizar dados
          </button>
        </div>
      </div>
    </div>
  )
}